import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Provider } from "@ethersproject/abstract-provider";
// import { Signer } from "@ethersproject/abstract-signer";
import { getNetwork } from "@ethersproject/networks";
import { Web3Provider } from "@ethersproject/providers";
import { useWeb3React } from "@web3-react/core";

import { isBatchedProvider, isWebSocketAugmentedProvider } from "@liquity/providers";
import {
  BlockPolledLiquityStore,
  EthersLiquity,
  EthersLiquityWithStore,
  _connectByChainId
} from "@liquity/lib-ethers";

import { LiquityFrontendConfig, getConfig } from "../config";

type LiquityContextValue = {
  config: LiquityFrontendConfig;
  account: string;
  provider: Provider;
  liquity: EthersLiquityWithStore<BlockPolledLiquityStore>;
};

const LiquityContext = createContext<LiquityContextValue | undefined>(undefined);

type LiquityProviderProps = {
  loader?: React.ReactNode;
  unsupportedNetworkFallback?: (chainId: number) => React.ReactNode;
  unsupportedMainnetFallback?: React.ReactNode;
};

// const wsParams = (network: string, infuraApiKey: string): [string, string] => [
//   `wss://${network === "homestead" ? "mainnet" : network}.infura.io/ws/v3/${infuraApiKey}`,
//   network
// ];

//Shinto: Websocket for pulse v4 and mumbai chain
const wsParams = (network: string, infuraApiKey: string): [string, string] => {
  let url, networkName;
  if (network === "mumbai") {
    url = `wss://matic.getblock.io/d8d15197-6d4a-4e2a-b732-b0e638f6ebd4/testnet/`;
    networkName = "pulse";
  } else if (network === "pulse") {
    // url = `wss://ws.v4.testnet.pulsechain.com`;
    url = `wss://matic.getblock.io/d8d15197-6d4a-4e2a-b732-b0e638f6ebd4/testnet/`;
    networkName = "pulse";
  } else if (network === "mainnet") {
    // url = `wss://rpc.pulsechain.com`;
    url = `wss://rpc-pulsechain.g4mm4.io`;
    networkName = "mainnet";
  } else {
    url = `wss://${network}.infura.io/ws/v3/${infuraApiKey}`;
    networkName = network;
  }
  return [url, networkName];
};

// Shinto: Supported Chain changed to mumbai only
// const webSocketSupportedNetworks = ["homestead", "kovan", "rinkeby", "ropsten", "goerli", "mumbai"];
const webSocketSupportedNetworks = ["pulse", "mumbai"];

export const LiquityProvider: React.FC<LiquityProviderProps> = ({
  children,
  loader,
  unsupportedNetworkFallback,
  unsupportedMainnetFallback
}) => {
  
  const { library: provider, account, chainId } = useWeb3React<Web3Provider>();
  const [config, setConfig] = useState<LiquityFrontendConfig>();

  const connection = useMemo(() => {
    if (config && provider && account && chainId) {
      try {
        return _connectByChainId(provider, provider.getSigner(account), chainId, {
          userAddress: account,
          frontendTag: config.frontendTag,
          useStore: "blockPolled"
        });
      } catch {}
    }
  }, [config, provider, account, chainId]);
    
  useEffect(() => {
    getConfig().then(setConfig);
  }, []);

  useEffect(() => {

    if (config && connection) {
      const { provider, chainId } = connection;

      if (isBatchedProvider(provider) && provider.chainId !== chainId) {
        provider.chainId = chainId;
      }

      if (isWebSocketAugmentedProvider(provider)) {
        const network = getNetwork(chainId);

        if (
          network.name &&
          webSocketSupportedNetworks.includes(network.name) &&
          config.infuraApiKey
        ) {
          provider.openWebSocket(...wsParams(network.name, config.infuraApiKey));
        } else if (connection._isDev) {
          provider.openWebSocket(`ws://${window.location.hostname}:8546`, chainId);
        }

        return () => {
          provider.closeWebSocket();
        };
      }
    }
  }, [config, connection]);

  if (!config || !provider || !account || !chainId) {
    return <>{loader}</>;
  }

  if (config.testnetOnly && chainId === 1) {
    return <>{unsupportedMainnetFallback}</>;
  }

  if (!connection) {
    return unsupportedNetworkFallback ? <>{unsupportedNetworkFallback(chainId)}</> : null;
  }

  const liquity = EthersLiquity._from(connection);
  liquity.store.logging = true;

  return (
    <LiquityContext.Provider value={{ config, account, provider, liquity }}>
      {children}
    </LiquityContext.Provider>
  );
};

export const useLiquity = () => {
  const liquityContext = useContext(LiquityContext);

  if (!liquityContext) {
    throw new Error("You must provide a LiquityContext via LiquityProvider");
  }

  return liquityContext;
};
